import Slider from '@ant-design/react-slick';
import type { Theme } from '@mui/material';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { FC } from 'react';

import { BrandCarouselSlide } from './BrandCarouselUtils';

import { SlickNextButton, SlickPreviousButton } from '~/components/molecules/SlickNavButton';
import { PAGE_CONTENT_MAX_WIDTH } from '~/constants';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';

import type { BrandCarouselProps } from '~/types/sanity/components';

export type CarouselProps = Omit<BrandCarouselProps, '_type'>;

const StyledPaging = styled(Box)(({ theme }) => ({
  button: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    cursor: 'pointer',
    opacity: 0.75,
  },

  '.slick-active > button': {
    opacity: 1,
  },
}));

export const BrandCarousel: FC<CarouselProps> = ({ title, slides, _key }) => {
  const getCompoundTestId = getCompoundTestIdGenerator('brandCarousel');
  const lgUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up('lg'));

  return (
    <Box>
      <Stack sx={{ maxWidth: PAGE_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        {title && (
          <Box sx={{ px: { xs: 2, sm: 14 }, py: 8 }}>
            <Typography sx={{ textAlign: 'center' }} variant="h3">
              {title}
            </Typography>
          </Box>
        )}
        <Box sx={{ pb: 8 }}>
          <Slider
            speed={500}
            dots
            autoplay
            arrows={lgUp}
            nextArrow={<SlickNextButton id={`brand-carousel-next-button-${_key}`} />}
            prevArrow={<SlickPreviousButton id={`brand-carousel-prev-button-${_key}`} />}
            appendDots={(dots) => <StyledPaging data-testid={getCompoundTestId('dots')}>{dots}</StyledPaging>}
            customPaging={(index) => (
              <button
                id={`brand-carousel-dot-${index + 1}-${_key}`}
                data-testid={getCompoundTestId('paging')}
                data-sp-button-label={`Go to slide ${index + 1}`}
              />
            )}
          >
            {slides.map((slide) => (
              <BrandCarouselSlide key={slide._key} {...slide} />
            ))}
          </Slider>
        </Box>
      </Stack>
    </Box>
  );
};
